/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useEffect } from "react";
import { TAB } from "dc-preview-utils/src/Constants/key-codes";
import { INTERACTIVE_ARIA_ROLES } from "dc-preview-utils/src/Constants/constants";
import PropTypes from "prop-types";

/**
 * Find DOM element with given id and focuses it
 * @param {string} elementId - ID of DOM element to be focussed
 */
const focusElement = elementId => {
  const element = document.getElementById(elementId);
  if (element) {
    element.focus();
  }
};

/**
 * Gets all children of a DOM element
 * @param {object} elementHandle - Element whose children have to be searched
 * @param {string} roles - Comma separated list of roles to be considered as children
 * @returns Array of DOM elements that are children of the given element
 */
const getChildren = (elementHandle, roles) => {
  if (elementHandle) {
    const children = elementHandle.querySelectorAll(roles);
    const childrenAr = Object.keys(children).map(key => children[key]);
    return childrenAr;
  }
  return [];
};

/**
 * Focuses first child of given DOM element
 * @param {object} elementHandle - Element whose first child should get focussed
 * @param {string} roles - Comma separated list of roles to be considered as children
 */
const focusFirstChild = (elementHandle, roles) => {
  const children = getChildren(elementHandle, roles);
  if (children.length) {
    children[0].focus();
  }
};

/**
 * Focuses first selected child of given DOM element
 * @param {object} elementHandle - Element whose first selected child should get focussed
 * @param {string} roles - Comma separated list of roles to be considered as children
 */
export const focusSelectedChild = (elementHandle, roles) => {
  const children = getChildren(elementHandle, roles);
  if (children.length) {
    let selectedChildIdx = children.findIndex(
      tabObj =>
        tabObj.ariaSelected === "true" ||
        tabObj.ariaCurrent !== "false" ||
        tabObj.ariaChecked === "true"
    );
    if (selectedChildIdx === -1) {
      selectedChildIdx = 0;
    }
    children[selectedChildIdx].focus();
  }
};

/**
 * Finds all children of given DOM element and selects the child
 * next to the current child that has focus
 * @param {object} elementHandle -
 * @param {string} roles - Comma separated list of roles to be considered as children
 * @param {object} event - (optional) - Event object
 */
export const focusNextChild = (
  elementHandle,
  roles,
  event,
  stopEventPropagation = false
) => {
  const children = getChildren(elementHandle, roles);
  if (children.length && children.includes(event.target)) {
    let activeChildIdx = children.findIndex(
      tabObj => tabObj === document.activeElement
    );

    activeChildIdx += 1;
    activeChildIdx %= children.length;
    children[activeChildIdx].focus();
    if (event) {
      event.preventDefault();
      if (stopEventPropagation) event.stopPropagation();
    }
  }
};

/**
 * Finds all children of given DOM element and selects the child
 * previous to the current child that has focus
 * @param {object} elementHandle -
 * @param {string} roles - Comma separated list of roles to be considered as children
 * @param {object} event - (optional) - Event object
 */
export const focusPreviousChild = (
  elementHandle,
  roles,
  event,
  stopEventPropagation = false
) => {
  const children = getChildren(elementHandle, roles);
  if (children.length && children.includes(event.target)) {
    let activeChildIdx = children.findIndex(
      tabObj => tabObj === document.activeElement
    );

    activeChildIdx -= 1;
    if (activeChildIdx < 0) {
      activeChildIdx = children.length - 1;
    }
    children[activeChildIdx].focus();
    if (event) {
      event.preventDefault();
      if (stopEventPropagation) event.stopPropagation();
    }
  }
};

/**
 * Method to keep keyboard focus trapped inside given DOM element.
 * Should get invoked on every key stroke
 * @param {object} elementHandle -
 * @param {string} roles - Comma separated list of roles to be considered as children
 * @param {object} event - Event object
 */
export const trapFocus = (
  elementHandle,
  roles = INTERACTIVE_ARIA_ROLES,
  evt
) => {
  const activeElement = document.activeElement;
  const interactiveElements = elementHandle.querySelectorAll(roles);

  const firstInteractiveElement = interactiveElements[0];
  const lastInteractiveElement =
    interactiveElements[interactiveElements.length - 1];

  if (evt.shiftKey) {
    if (activeElement === firstInteractiveElement) {
      lastInteractiveElement.focus();
      evt.preventDefault();
    }
  } else if (activeElement === lastInteractiveElement) {
    firstInteractiveElement.focus();
    evt.preventDefault();
  }
};

const FocusTrapComponent = React.forwardRef((props, ref) =>
  React.Children.map(props.children, child =>
    React.cloneElement(child, { ...props, forwardedRef: ref })
  )
);

/**
 * HOC that creates a ref and injects it into the component it wraps.
 * The ref is later used to intercept keyboard events and trap them if required.
 * @param {object} children - Component(s) to be wrapped
 * @param {string} roles - Comma separated list of roles to be considered as children
 * @returns HOC that captures keyboard focus
 */
export const FocusTrap = ({ children, roles }) => {
  const ref = React.createRef();
  let triggerElementId;

  useEffect(() => {
    triggerElementId = document.activeElement.id;
    const elementHandle = ref.current;
    if (elementHandle) {
      focusFirstChild(elementHandle, roles);
      elementHandle.onkeydown = evt => {
        const { keyCode } = evt;
        switch (keyCode) {
          case TAB:
            trapFocus(elementHandle, roles, evt);
            break;
          default:
        }
      };
    }
    return () => {
      if (triggerElementId) {
        focusElement(triggerElementId);
      }
    };
  }, []);

  return <FocusTrapComponent ref={ref}>{children}</FocusTrapComponent>;
};

FocusTrap.propTypes = {
  children: PropTypes.objectOf(PropTypes.element),
  roles: PropTypes.string.isRequired
};

FocusTrap.defaultProps = {
  children: undefined
};

FocusTrapComponent.propTypes = {
  children: PropTypes.objectOf(PropTypes.element)
};

FocusTrapComponent.defaultProps = {
  children: undefined
};
